'use client';

import { useState, useCallback, useEffect } from 'react';
import { Activity, Scale, Target, Zap, Info, Plus, Minus } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { calorieCalculatorContent } from '../../data/calorie-calculator-content';
import Link from 'next/link';

type Gender = 'male' | 'female';
type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'veryActive';
type Goal = 'lose' | 'maintain' | 'gain';

const ACTIVITY_MULTIPLIER = {
  sedentary: 1.2,      // Little to no exercise
  light: 1.375,       // Light exercise 1-3 days/week
  moderate: 1.55,     // Moderate exercise 3-5 days/week
  active: 1.725,      // Hard exercise 6-7 days/week
  veryActive: 1.9,    // Very hard exercise & physical job or 2x training
};

const GOAL_MULTIPLIER = {
  lose: 0.85,    // 15% calorie deficit
  maintain: 1,   // Maintain weight
  gain: 1.15     // 15% calorie surplus
};

const MACRO_RATIOS = {
  balanced: { protein: 0.3, carbs: 0.4, fat: 0.3 },
  lowCarb: { protein: 0.4, carbs: 0.2, fat: 0.4 },
  lowFat: { protein: 0.3, carbs: 0.5, fat: 0.2 },
  highProtein: { protein: 0.4, carbs: 0.3, fat: 0.3 },
};

type MacroType = keyof typeof MACRO_RATIOS;

export default function CalorieCalculator() {
  // User inputs
  const [age, setAge] = useState<number>(25);
  const [gender, setGender] = useState<Gender>('male');
  const [weight, setWeight] = useState<number>(70);
  const [height, setHeight] = useState<number>(175);
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('moderate');
  const [goal, setGoal] = useState<Goal>('maintain');
  const [macroPreference, setMacroPreference] = useState<MacroType>('balanced');

  // Results
  const [bmr, setBmr] = useState<number>(0);
  const [tdee, setTdee] = useState<number>(0);
  const [dailyCalories, setDailyCalories] = useState<number>(0);
  const [macros, setMacros] = useState({
    protein: 0,
    carbs: 0,
    fat: 0,
  });

  // Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor Equation
  const calculateBmr = useCallback(() => {
    // BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age in years) + s
    // where s is +5 for males and -161 for females
    const genderFactor = gender === 'male' ? 5 : -161;
    return (10 * weight) + (6.25 * height) - (5 * age) + genderFactor;
  }, [age, gender, height, weight]);

  // Calculate TDEE (Total Daily Energy Expenditure)
  const calculateTdee = useCallback((bmr: number) => {
    return Math.round(bmr * ACTIVITY_MULTIPLIER[activityLevel]);
  }, [activityLevel]);

  // Calculate daily calorie target based on goal
  const calculateDailyCalories = useCallback((tdee: number) => {
    return Math.round(tdee * GOAL_MULTIPLIER[goal]);
  }, [goal]);

  // Calculate macronutrients
  const calculateMacros = useCallback((calories: number) => {
    const ratio = MACRO_RATIOS[macroPreference];

    // 1g protein = 4 calories, 1g carbs = 4 calories, 1g fat = 9 calories
    const proteinGrams = Math.round((calories * ratio.protein) / 4);
    const fatGrams = Math.round((calories * ratio.fat) / 9);
    const remainingCalories = calories - (proteinGrams * 4) - (fatGrams * 9);
    const carbGrams = Math.round(remainingCalories / 4);

    return {
      protein: proteinGrams,
      carbs: carbGrams,
      fat: fatGrams,
    };
  }, [macroPreference]);

  // Recalculate when inputs change
  useEffect(() => {
    const userBmr = calculateBmr();
    const userTdee = calculateTdee(userBmr);
    const userDailyCalories = calculateDailyCalories(userTdee);
    const userMacros = calculateMacros(userDailyCalories);

    setBmr(Math.round(userBmr));
    setTdee(userTdee);
    setDailyCalories(userDailyCalories);
    setMacros(userMacros);
  }, [age, gender, weight, height, activityLevel, goal, macroPreference, calculateBmr, calculateTdee, calculateDailyCalories, calculateMacros]);

  // Handle input changes with validation
  const handleNumberInput = (e: React.ChangeEvent<HTMLInputElement>, setter: (value: number) => void, min: number, max: number) => {
    const value = e.target.value;

    // Allow empty string for better UX when deleting numbers
    if (value === '') {
      setter(min);
      return;
    }

    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const num = parseInt(value, 10);

    // Update the state with the raw value first (to allow typing)
    setter(isNaN(num) ? min : Math.min(Math.max(num, min), max));
  };

  // Get goal description
  const getGoalDescription = () => {
    switch (goal) {
      case 'lose':
        return 'Weight Loss (15% deficit)';
      case 'maintain':
        return 'Weight Maintenance';
      case 'gain':
        return 'Muscle Gain (15% surplus)';
      default:
        return '';
    }
  };

  // Get activity level description
  const getActivityDescription = (level: ActivityLevel) => {
    const descriptions = {
      sedentary: 'Little to no exercise',
      light: 'Light exercise 1-3 days/week',
      moderate: 'Moderate exercise 3-5 days/week',
      active: 'Hard exercise 6-7 days/week',
      veryActive: 'Very hard exercise & physical job or 2x training',
    };
    return descriptions[level];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 py-8 px-4 sm:py-12">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 px-2">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-purple-500" />
          </div>
          <ToolPageIntro title="Calorie Calculator">
                Calculate daily calorie needs based on your age, gender, height, weight, and activity level. Our free calorie calculator helps with weight management and nutrition planning.
            </ToolPageIntro>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Scale className="w-5 h-5 mr-2 text-purple-500" />
                Your Stats
              </h2>

              <div className="space-y-4">
                {/* Age */}
                <div>
                  <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Age
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="age"
                      min="15"
                      max="100"
                      value={age}
                      onChange={(e) => setAge(Number(e.target.value))}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 15) setAge(15);
                        else if (value > 100) setAge(100);
                        else setAge(value);
                      }}
                      className="block w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      years
                    </span>
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gender
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {(['male', 'female'] as const).map((g) => (
                      <button
                        key={g}
                        onClick={() => setGender(g)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${gender === g
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                      >
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Weight */}
                <div>
                  <label htmlFor="weight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Weight
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="weight"
                      min="30"
                      max="300"
                      value={weight}
                      onChange={(e) => setWeight(Number(e.target.value))}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 30) setWeight(30);
                        else if (value > 300) setWeight(300);
                        else setWeight(value);
                      }}
                      className="block w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      kg
                    </span>
                  </div>
                </div>

                {/* Height */}
                <div>
                  <label htmlFor="height" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Height
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="height"
                      min="100"
                      max="250"
                      value={height}
                      onChange={(e) => setHeight(Number(e.target.value))}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 100) setHeight(100);
                        else if (value > 250) setHeight(250);
                        else setHeight(value);
                      }}
                      className="block w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      cm
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Level */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-purple-500" />
                Activity Level
              </h2>

              <div className="space-y-2">
                {([
                  { value: 'sedentary', label: 'Sedentary' },
                  { value: 'light', label: 'Lightly Active' },
                  { value: 'moderate', label: 'Moderately Active' },
                  { value: 'active', label: 'Very Active' },
                  { value: 'veryActive', label: 'Extremely Active' },
                ] as const).map(({ value, label }) => (
                  <div
                    key={value}
                    onClick={() => setActivityLevel(value)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${activityLevel === value
                      ? 'bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800'
                      : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600/50 border border-transparent'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${activityLevel === value ? 'text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-200'
                        }`}>
                        {label}
                      </span>
                      {activityLevel === value && (
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                      {getActivityDescription(value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Goal */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-purple-500" />
                Your Goal
              </h2>

              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'lose', label: 'Lose', emoji: '👇' },
                  { value: 'maintain', label: 'Maintain', emoji: '✋' },
                  { value: 'gain', label: 'Gain', emoji: '👆' },
                ] as const).map(({ value, label, emoji }) => (
                  <button
                    key={value}
                    onClick={() => setGoal(value)}
                    className={`py-3 rounded-lg flex flex-col items-center justify-center transition-colors ${goal === value
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                  >
                    <span className="text-xl mb-1">{emoji}</span>
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Macronutrient Split
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(MACRO_RATIOS).map(([key, { protein, carbs, fat }]) => {
                    const isActive = macroPreference === key;
                    const displayName = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase());

                    return (
                      <button
                        key={key}
                        onClick={() => setMacroPreference(key as MacroType)}
                        className={`p-2 text-xs rounded-lg transition-colors ${isActive
                          ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
                          : 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600/50 border border-transparent'
                          }`}
                      >
                        <div className="font-medium mb-1">{displayName}</div>
                        <div className="text-2xs text-gray-500 dark:text-gray-400">
                          P{Math.round(protein * 100)}% C{Math.round(carbs * 100)}% F{Math.round(fat * 100)}%
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Your Daily Calorie & Macronutrient Targets
              </h2>

              <div className="space-y-6">
                {/* Calorie Summary */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">
                      {getGoalDescription()}
                    </h3>
                    <div className="px-3 py-1 bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-200 text-sm font-medium rounded-full">
                      {dailyCalories.toLocaleString()} kcal/day
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">BMR (Basal Metabolic Rate)</span>
                      <span className="font-medium text-gray-900 dark:text-white">{bmr.toLocaleString()} kcal/day</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">TDEE (Total Daily Energy Expenditure)</span>
                      <span className="font-medium text-gray-900 dark:text-white">{tdee.toLocaleString()} kcal/day</span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-purple-100 dark:border-purple-800">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Daily Calorie Target</span>
                        <span className="text-lg font-bold text-purple-700 dark:text-purple-200">
                          {dailyCalories.toLocaleString()} kcal
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Macros Breakdown */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    MACRONUTRIENT TARGETS
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Protein */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-300">PROTEIN</span>
                        <span className="text-xs font-bold text-blue-700 dark:text-blue-200">{macros.protein}g</span>
                      </div>
                      <div className="w-full bg-blue-100 dark:bg-blue-800/30 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-blue-600 dark:text-blue-300">
                        {(macros.protein * 4).toLocaleString()} kcal
                      </div>
                    </div>

                    {/* Carbs */}
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-green-700 dark:text-green-300">CARBS</span>
                        <span className="text-xs font-bold text-green-700 dark:text-green-200">{macros.carbs}g</span>
                      </div>
                      <div className="w-full bg-green-100 dark:bg-green-800/30 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-green-600 dark:text-green-300">
                        {(macros.carbs * 4).toLocaleString()} kcal
                      </div>
                    </div>

                    {/* Fat */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">FAT</span>
                        <span className="text-xs font-bold text-yellow-700 dark:text-yellow-200">{macros.fat}g</span>
                      </div>
                      <div className="w-full bg-yellow-100 dark:bg-yellow-800/30 rounded-full h-2">
                        <div
                          className="bg-yellow-500 h-2 rounded-full"
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-yellow-600 dark:text-yellow-300">
                        {(macros.fat * 9).toLocaleString()} kcal
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meal Planning Tips */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <Info className="w-4 h-4 mr-1.5 text-gray-500" />
                    MEAL PLANNING TIPS
                  </h3>
                  <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5">
                    <li className="flex items-start">
                      <span className="mr-1.5">•</span>
                      <span>Eat {Math.ceil(macros.protein / 3)}-{Math.ceil(macros.protein / 4)}g of protein per meal</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-1.5">•</span>
                      <span>Include a source of healthy fats with each meal</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-1.5">•</span>
                      <span>Choose complex carbs like whole grains and vegetables</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-1.5">•</span>
                      <span>Stay hydrated - aim for at least {Math.round(weight * 0.033 * 1000)}ml of water daily</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Nutrition Tips */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-5 sm:p-6">
              <h3 className="text-sm font-semibold text-purple-800 dark:text-purple-200 mb-3 flex items-center">
                <Info className="w-4 h-4 mr-2 text-purple-500" />
                NUTRITION TIPS FOR {goal.toUpperCase()}ING WEIGHT
              </h3>

              {goal === 'lose' && (
                <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Prioritize protein</strong> to maintain muscle mass while in a calorie deficit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Eat more fiber-rich foods</strong> to stay full longer (vegetables, fruits, whole grains)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Stay hydrated</strong> - sometimes thirst is mistaken for hunger</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Get enough sleep</strong> - poor sleep can affect hunger hormones</span>
                  </li>
                </ul>
              )}

              {goal === 'maintain' && (
                <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Focus on food quality</strong> - choose nutrient-dense whole foods</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Practice portion control</strong> to maintain your current weight</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Stay active</strong> - aim for at least 150 minutes of moderate exercise per week</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Monitor your weight</strong> and adjust calories as needed</span>
                  </li>
                </ul>
              )}

              {goal === 'gain' && (
                <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Eat in a calorie surplus</strong> of 250-500 calories above maintenance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Prioritize protein intake</strong> (1.6-2.2g per kg of body weight) for muscle growth</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Time your meals</strong> - consume protein and carbs around workouts</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-1.5">•</span>
                    <span><strong>Progressive overload</strong> in your workouts to stimulate muscle growth</span>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12 max-w-5xl mx-auto">
        <InformationalContent
          title={calorieCalculatorContent.title}
          description={calorieCalculatorContent.description}
          content={calorieCalculatorContent.content}
          faqs={calorieCalculatorContent.faqs}
          relatedTools={calorieCalculatorContent.relatedTools}
        />
      </div>
    </div>
  );
}
